export const TermsAndConditionsContent = () => {
  return (
    <div className="space-y-4 text-sm leading-6">
      <h1 className="text-lg font-bold uppercase">NutriCoach Terms and Conditions</h1>
      <p className="text-xs font-semibold">Effective date: January 2026</p>

      <section className="space-y-2">
        <p>Please read these Terms and Conditions carefully before using NutriCoach.</p>
        <p>
          These Terms and Conditions govern your access to and use of the NutriCoach mobile
          application, website, content, features and related services made available by{" "}
          <strong>NUTRICOACH LIMITED</strong>, company number <strong>14709771</strong>, whose
          registered office is{" "}
          <strong>79 The Mount, Hale Barns, Cheshire, WA15 8SZ, United Kingdom</strong> (
          <strong>"NutriCoach"</strong>, <strong>"we"</strong>, <strong>"us"</strong> or{" "}
          <strong>"our"</strong>).
        </p>
        <p>
          By creating an account, starting a free trial, purchasing a subscription, accessing or
          using NutriCoach, you agree to be bound by these Terms and Conditions and our Privacy
          Policy. If you do not agree, you must not use NutriCoach.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">1. About NutriCoach</h2>
        <p>
          1.1 NutriCoach is a digital, lifestyle-led nutrition platform designed to provide general
          informational content, food planning tools, meal structure support, educational content,
          food suggestions, organisational features and related wellness functionality.
        </p>
        <p>
          1.2 NutriCoach is intended for general lifestyle and wellbeing purposes only. It is not a
          medical service, dietetic service, therapy service, or emergency support service.
        </p>
        <p>
          1.3 NutriCoach is offered globally, although it is initially intended primarily for UK
          users. If you access the Platform from outside the UK, you are responsible for ensuring
          your use complies with local laws.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">2. Important Health and Medical Disclaimer</h2>
        <p>
          2.1 <strong>NutriCoach does not provide medical advice, diagnosis or treatment.</strong>{" "}
          Nothing in the app, website, onboarding flow, meal suggestions, calorie targets, portion
          guidance, educational materials, chatbot outputs, notifications or any other content
          provided through NutriCoach is intended to amount to:
        </p>
        <ol className="list-[lower-alpha] space-y-1 pl-5">
          <li>medical advice;</li>
          <li>diagnosis;</li>
          <li>treatment;</li>
          <li>nutritional therapy;</li>
          <li>mental health advice or treatment;</li>
          <li>emergency guidance; or</li>
          <li>
            a substitute for advice from a doctor, dietitian, nutritionist, pharmacist, therapist or
            any other appropriately qualified healthcare professional.
          </li>
        </ol>
        <p>
          2.2 NutriCoach does not guarantee that any feature, suggestion, calculation, output or
          content is suitable for your individual circumstances.
        </p>
        <p>
          2.3 You are solely responsible for deciding whether NutriCoach is appropriate for you and
          whether any output, suggestion, food item, portion, calorie target, macro target or other
          nutritional information is right for you.
        </p>
        <p>
          2.4 You should seek advice from your GP, doctor, registered dietitian, qualified nutrition
          professional or other appropriate healthcare professional before using NutriCoach, and at
          any time during use, if you have any concern whatsoever about your health, symptoms,
          medications, nutritional needs, mental wellbeing, eating behaviours or suitability to use
          the Platform.
        </p>
        <p>
          2.5 If you feel unwell, distressed, psychologically triggered, or believe the Platform may
          be unsuitable for you for any reason, you must stop using NutriCoach and seek appropriate
          professional advice.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">3. Users for Whom NutriCoach May Be Unsuitable</h2>
        <p>3.1 NutriCoach is not intended for use by anyone under the age of 18.</p>
        <p>
          3.2 You must not use NutriCoach, or must only use it after obtaining advice from a
          suitably qualified healthcare professional, if you:
        </p>
        <ol className="list-[lower-alpha] space-y-1 pl-5">
          <li>are pregnant;</li>
          <li>are trying to conceive and require tailored nutrition support;</li>
          <li>are breastfeeding;</li>
          <li>
            have, or have previously had, an eating disorder or disordered eating, including
            anorexia, bulimia, binge eating disorder, orthorexia or compulsive calorie-related
            behaviours;
          </li>
          <li>
            have a mental health condition, including anxiety, OCD, depression, body dysmorphia or
            any other condition where food, weight or calorie-focused tools may worsen symptoms;
          </li>
          <li>
            have diabetes, particularly where medication, insulin or blood sugar management may be
            affected by dietary change;
          </li>
          <li>
            have kidney disease, liver disease, cancer, gastrointestinal disease, malabsorption
            conditions, severe food allergies, metabolic disease, or any other illness or condition
            requiring therapeutic or clinically supervised nutrition;
          </li>
          <li>
            are taking medication that may affect appetite, digestion, body weight, metabolism or
            nutritional requirements;
          </li>
          <li>
            are underweight, medically vulnerable, or have been advised by a healthcare professional
            not to engage in calorie-focused or weight-management tools; or
          </li>
          <li>
            have any other condition which makes general lifestyle nutrition tools unsuitable.
          </li>
        </ol>
        <p>3.3 By using NutriCoach, you confirm that:</p>
        <ol className="list-[lower-alpha] space-y-1 pl-5">
          <li>you understand the limitations of the Platform;</li>
          <li>
            you are responsible for assessing whether the Platform is appropriate for you; and
          </li>
          <li>
            you will not rely on NutriCoach as a medical, dietetic, therapeutic or emergency
            service.
          </li>
        </ol>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">4. Nutrition Content and Professional Oversight</h2>
        <p>
          4.1 NutriCoach may develop or review certain content with input from a Head of Nutrition
          Standards and Behaviour qualified in Nutrition and Weight Management through the Royal
          Society of Public Health.
        </p>
        <p>
          4.2 Any such oversight, review or qualification does <strong>not</strong> mean that
          NutriCoach provides personalised medical care, dietetic treatment, clinical nutrition
          therapy, diagnosis or regulated healthcare services.
        </p>
        <p>
          4.3 Any information or standards used within NutriCoach remain general in nature and may
          not be appropriate for your personal circumstances.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">5. Accuracy, Estimates and App Limitations</h2>
        <p>
          5.1 NutriCoach may provide calorie figures, nutritional estimates, portion suggestions,
          meal structures, food swaps, educational content and similar outputs. These are{" "}
          <strong>indicative only</strong>.
        </p>
        <p>
          5.2 Such outputs may rely on assumptions, automated logic, third-party data, food database
          information, product labelling, user-entered information, standard food composition
          references and other sources which may be incomplete, inaccurate or out of date.
        </p>
        <p>5.3 We do not warrant or guarantee that any:</p>
        <ol className="list-[lower-alpha] space-y-1 pl-5">
          <li>calorie target;</li>
          <li>calorie range;</li>
          <li>macro target;</li>
          <li>portion guide;</li>
          <li>nutritional estimate;</li>
          <li>ingredient value;</li>
          <li>food substitution;</li>
          <li>suggested meal structure;</li>
          <li>bodyweight-related output; or</li>
          <li>any other content or output</li>
        </ol>
        <p>
          is accurate, complete, current, clinically appropriate, suitable for you, or free from
          error.
        </p>
        <p>
          5.4 Nutritional information can vary materially due to brands, recipes, ingredients,
          preparation method, cooking method, serving sizes, restaurant variation, reformulations,
          user input errors and other factors.
        </p>
        <p>
          5.5 You remain solely responsible for deciding whether any recommendation or information
          shown in NutriCoach is appropriate for you before acting on it.
        </p>
        <p>
          5.6 The Platform may contain bugs, downtime, sync problems, duplicated data, food database
          anomalies, incorrect calculations, missing information, technical faults, display issues
          or other errors. You must not rely on the Platform as your sole source of health or
          nutrition decision-making.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">6. Eligibility and User Accounts</h2>
        <p>
          6.1 To use NutriCoach, you must create an account and provide accurate, current and
          complete information.
        </p>
        <p>6.2 You are responsible for keeping your account credentials secure and confidential.</p>
        <p>6.3 You are responsible for all activity carried out through your account.</p>
        <p>
          6.4 You must notify us promptly at <strong>hello@nutricoach.co.uk</strong> if you believe
          your account has been accessed without your permission or there has been any security
          breach.
        </p>
        <p>
          6.5 We may refuse registration, suspend access, or close accounts where reasonably
          necessary, including where:
        </p>
        <ol className="list-[lower-alpha] space-y-1 pl-5">
          <li>you breach these Terms;</li>
          <li>we suspect misuse, fraud or unlawful activity;</li>
          <li>your use may create risk for us, other users or third parties; or</li>
          <li>we believe the Platform is unsuitable for you.</li>
        </ol>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">7. Subscription, Free Trial and Billing</h2>
        <p>
          7.1 NutriCoach is offered on a subscription basis through the{" "}
          <strong>Apple App Store</strong> and <strong>Google Play Store</strong> only.
        </p>
        <p>
          7.2 We may offer a <strong>7-day free trial</strong>. Unless cancelled before the trial
          ends, your subscription will automatically convert into a paid subscription through the
          relevant app store using the payment method registered with that app store.
        </p>
        <p>
          7.3 Subscription prices, billing cycles and available plans will be shown to you before
          you subscribe.
        </p>
        <p>
          7.4 Your subscription will automatically renew unless cancelled in accordance with the
          rules and processes of the relevant app store.
        </p>
        <p>
          7.5 By starting a trial or subscription, you authorise the relevant app store to charge
          the applicable subscription fees in accordance with its billing terms.
        </p>
        <p>
          7.6 We may change pricing, plans or features from time to time. Any pricing change will
          apply prospectively unless otherwise required by law or app store rules.
        </p>
        <p>
          7.7 App Store and Google Play billing, renewals, cancellations, refunds and payment
          disputes are handled by the applicable platform provider to the extent required by their
          systems and policies. You are responsible for managing your subscription settings in your
          Apple or Google account where relevant.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">8. Cancellation and Refunds</h2>
        <p>
          8.1 You may cancel your subscription at any time through the Apple App Store or Google
          Play Store subscription settings, depending on how you subscribed.
        </p>
        <p>
          8.2 Unless stated otherwise in a promotion or required by law, cancelling your
          subscription stops future renewals but does not usually entitle you to a refund for the
          current billing period.
        </p>
        <p>
          8.3 Any refund request relating to an in-app purchase made through Apple App Store or
          Google Play Store must be directed through the relevant store process, unless local law
          requires otherwise.
        </p>
        <p>
          8.4 Where UK consumer law gives you cancellation rights, those rights apply only to the
          extent they have not lawfully been lost, limited or modified because you expressly
          requested immediate access to digital content or services and acknowledged the
          consequences of that request.
        </p>
        <p>
          8.5 It is your responsibility to cancel before renewal if you do not want the next
          subscription payment to be taken.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">9. Acceptable Use</h2>
        <p>9.1 You may use NutriCoach only for your own personal, non-commercial use.</p>
        <p>9.2 You must not:</p>
        <ol className="list-[lower-alpha] space-y-1 pl-5">
          <li>copy, scrape, harvest or extract data from the Platform;</li>
          <li>resell, sublicense or commercially exploit NutriCoach;</li>
          <li>
            reverse engineer, decompile or attempt to access source code except where legally
            permitted;
          </li>
          <li>interfere with the operation or security of the Platform;</li>
          <li>upload malicious code, malware or harmful material;</li>
          <li>use NutriCoach in any unlawful, abusive, fraudulent or misleading way;</li>
          <li>impersonate another person;</li>
          <li>share your login with another person; or</li>
          <li>use the Platform in a way that competes with our business.</li>
        </ol>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">10. Content and Intellectual Property</h2>
        <p>
          10.1 All intellectual property rights in NutriCoach, including the app, design, branding,
          software, content, graphics, databases, text, features and materials, belong to us or our
          licensors.
        </p>
        <p>
          10.2 We grant you a limited, non-exclusive, non-transferable, revocable licence to use
          NutriCoach for personal, non-commercial use in accordance with these Terms.
        </p>
        <p>
          10.3 You must not copy, reproduce, adapt, distribute, publish, sell, licence or exploit
          any part of NutriCoach except as expressly permitted by us in writing.
        </p>
        <p>
          10.4 If you submit comments, feedback, suggestions, photographs, messages or other content
          to us, you grant us a non-exclusive, worldwide, royalty-free licence to use, reproduce and
          display that content for operating, improving and promoting NutriCoach, subject always to
          our Privacy Policy and applicable law.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">11. Privacy and Data Protection</h2>
        <p>11.1 We process personal data in accordance with our Privacy Policy.</p>
        <p>
          11.2 NutriCoach may collect and process information you provide about your weight, goals,
          food preferences, habits, allergies, health-related circumstances and other information
          relevant to your use of the Platform.
        </p>
        <p>
          11.3 Some of that information may amount to <strong>health data</strong> or other{" "}
          <strong>special category data</strong> under UK data protection law, which requires
          additional protection.
        </p>
        <p>
          11.4 To provide NutriCoach&apos;s AI coaching and voice features, we use third-party
          processing services. These currently include <strong>OpenAI</strong> and{" "}
          <strong>Google (Gemini)</strong> for AI-powered coaching, and <strong>Groq</strong> for
          voice transcription.
        </p>
        <p>
          11.5 Depending on the feature you choose to use, information sent to these providers may
          include your messages, food or recipe photographs, voice recordings, meal-plan context,
          nutrition and profile information relevant to your request, and non-identifying technical
          information needed to operate and secure the service. We do not intentionally send direct
          account identifiers such as your name, email address, or NutriCoach user ID to these
          providers. However, content you choose to type, upload, or record may contain identifying
          information if you include it. OpenAI and Google process relevant content to generate AI
          responses, while Groq processes voice recordings to produce text transcriptions.
        </p>
        <p>
          11.6 By accepting these Terms and our Privacy Policy, you explicitly authorise us to share
          this information with the providers identified above for those purposes. You can withdraw
          this permission at any time by contacting us at <strong>hello@nutricoach.co.uk</strong>.
          Withdrawing permission will not affect processing already carried out, but it may prevent
          you from using AI coaching, voice transcription, or other features that depend on this
          processing.
        </p>
        <p>
          11.7 We require service providers that process personal data for us to protect it in line
          with applicable law and our contractual requirements. More information about the data we
          share, why we share it, how long it is kept, and your rights is set out in our Privacy
          Policy.
        </p>
        <p>
          11.8 You confirm that any information you provide to us is accurate to the best of your
          knowledge and that you will update it if relevant information changes.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">12. Availability and Changes to the Platform</h2>
        <p>
          12.1 We do not guarantee that NutriCoach will always be available, uninterrupted, secure
          or error-free.
        </p>
        <p>
          12.2 We may update, suspend, remove, replace or discontinue any feature, content or
          functionality of NutriCoach at any time.
        </p>
        <p>
          12.3 We may release patches, updates or new versions of the app, and continued use may
          require you to install updates.
        </p>
        <p>
          12.4 We are not responsible for delays, outages or failures caused by app stores, device
          manufacturers, internet providers, hosting providers or other third parties.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">13. Third-Party Services and Stores</h2>
        <p>
          13.1 NutriCoach may rely on or integrate with third-party platforms, including Apple App
          Store and Google Play Store.
        </p>
        <p>
          13.2 We are not responsible for third-party products, services, policies, software,
          payment systems, uptime or content.
        </p>
        <p>
          13.3 Your use of third-party services may also be subject to that third party&apos;s terms
          and privacy policies.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">14. Limitation of Liability</h2>
        <p>
          14.1 Nothing in these Terms excludes or limits liability that cannot lawfully be excluded
          or limited under applicable law, including liability for death or personal injury caused
          by negligence, fraud, or any other liability that the law does not permit us to exclude.
        </p>
        <p>
          14.2 Subject to clause 14.1, NutriCoach is provided on an "as is" and "as available"
          basis.
        </p>
        <p>14.3 Subject to clause 14.1, we do not accept liability for:</p>
        <ol className="list-[lower-alpha] space-y-1 pl-5">
          <li>
            any loss arising from reliance on calorie figures, nutritional information, food
            suggestions, educational content or app outputs;
          </li>
          <li>
            any health outcome, weight change, allergic reaction, nutritional deficiency, adverse
            symptom, illness, distress, mental health impact, or worsening of any condition arising
            from use of or reliance on the Platform;
          </li>
          <li>any indirect or consequential loss;</li>
          <li>loss of profit, revenue, opportunity, goodwill or data;</li>
          <li>
            app unavailability, bugs, anomalies, syncing issues, database inaccuracies or technical
            malfunction; or
          </li>
          <li>any acts or omissions of any app store, payment provider or third-party service.</li>
        </ol>
        <p>
          14.4 Subject to clause 14.1, you use NutriCoach entirely at your own risk and remain
          solely responsible for decisions you make based on content or outputs provided through the
          Platform.
        </p>
        <p>
          14.5 Subject to clause 14.1, our total aggregate liability to you arising out of or in
          connection with NutriCoach shall not exceed the total subscription fees actually paid by
          you to use NutriCoach in the 12 months immediately preceding the event giving rise to the
          claim.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">15. Indemnity</h2>
        <p>
          15.1 You agree to indemnify and hold harmless NutriCoach, its directors, officers,
          employees and contractors from and against claims, losses, liabilities, damages, costs and
          expenses arising out of:
        </p>
        <ol className="list-[lower-alpha] space-y-1 pl-5">
          <li>your misuse of the Platform;</li>
          <li>your breach of these Terms;</li>
          <li>your breach of law; or</li>
          <li>
            your reliance on the Platform in circumstances where these Terms make clear that
            professional advice should have been sought,
          </li>
        </ol>
        <p>
          except to the extent caused by our negligence or other liability that cannot legally be
          excluded.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">16. Suspension and Termination</h2>
        <p>16.1 We may suspend or terminate your access to NutriCoach immediately where:</p>
        <ol className="list-[lower-alpha] space-y-1 pl-5">
          <li>you breach these Terms;</li>
          <li>we reasonably suspect fraud, abuse or unlawful activity;</li>
          <li>
            we reasonably believe your use may create legal, technical or reputational risk; or
          </li>
          <li>we reasonably believe NutriCoach is unsuitable for you.</li>
        </ol>
        <p>
          16.2 You may stop using NutriCoach at any time. Terminating use of the app does not itself
          cancel an app store subscription. You must also cancel via the relevant app store if you
          do not want renewal charges.
        </p>
        <p>
          16.3 On termination, your right to access and use NutriCoach will cease, but clauses
          intended to survive termination will continue, including those relating to liability,
          intellectual property, disputes and payment obligations already accrued.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">17. Changes to These Terms</h2>
        <p>17.1 We may update these Terms from time to time.</p>
        <p>
          17.2 Where changes are material, we will take reasonable steps to notify users, for
          example through the app, by email or on our website.
        </p>
        <p>
          17.3 By continuing to use NutriCoach after updated Terms take effect, you agree to the
          revised Terms.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">18. Governing Law and Jurisdiction</h2>
        <p>
          18.1 These Terms and any dispute or claim arising out of or in connection with them shall
          be governed by the laws of England and Wales.
        </p>
        <p>
          18.2 If you are a consumer resident in Scotland or Northern Ireland, you may also have the
          right to bring proceedings in your home jurisdiction.
        </p>
        <p>
          18.3 If you are a consumer resident in England and Wales, the courts of England and Wales
          shall have jurisdiction, subject to any mandatory rights you may have under applicable
          consumer law.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-semibold uppercase">19. Contact Details</h2>
        <p>If you have questions about these Terms, please contact:</p>
        <address className="space-y-1 not-italic">
          <p>
            <strong>NUTRICOACH LIMITED</strong>
          </p>
          <p>
            Company number: <strong>14709771</strong>
          </p>
          <p>
            Registered office:{" "}
            <strong>79 The Mount, Hale Barns, Cheshire, WA15 8SZ, United Kingdom</strong>
          </p>
          <p>
            Email: <strong>hello@nutricoach.co.uk</strong>
          </p>
        </address>
      </section>
    </div>
  );
};
